import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import {
  WorkspaceAsset,
  WorkspaceRoutine,
  useFilteredWorkspaceRoutines,
  useUpdateMutation,
} from '@gripp/shared-logic';
import _ from 'lodash';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ScrollView,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { Text } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import Sortable, { SortableGridDragEndParams } from 'react-native-sortables';
import { Spacing } from '../../../styles';
import { Colors } from '../../../themes/colors';
import { extractAndDeduplicateRoutines } from '../../../utils/assetHelpers';
import { Button } from '../../crop-circle/Button';
import { SearchInput } from '../../crop-circle/Components/SearchInput';
import { Icon } from '../../crop-circle/icons';
import { spacing } from '../../crop-circle/tokens';
import { BottomComponent } from '../../navigation/types';
import { useSystemNotification } from '../../system-notification/system-notification-provider';
import { RequestRoutineModal } from './requestRoutine';

type EditRoutineModalProps = {
  workspaceAsset: WorkspaceAsset;
  editRoutineModalVisible: boolean;
  onClose: () => void;
  modalStyle?: StyleProp<ViewStyle>;
  isWeb?: boolean;
  bottomBar?: BottomComponent;
  /** Controlled: open the request-routine overlay from the parent. */
  requestRoutineVisible?: boolean;
  /** Controlled: called when the request-routine overlay should close. */
  onRequestRoutineClose?: () => void;
};

export const EditRoutineModal: React.FC<EditRoutineModalProps> = ({
  onClose,
  workspaceAsset,
  editRoutineModalVisible,
  modalStyle,
  isWeb,
  bottomBar,
  requestRoutineVisible: controlledRequestRoutineVisible,
  onRequestRoutineClose,
}) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSystemNotification();
  const [selectedRoutines, setSelectedRoutines] = useState<RoutineButtonData[]>(
    []
  );
  const initialSelectedRoutines = useRef<RoutineButtonData[] | undefined>(
    undefined
  );
  const isSavingRef = useRef(false);
  const pendingSaveRef = useRef<{
    routineData: RoutineButtonData[];
    showSuccessMessage: boolean;
  } | null>(null);
  const [isDraggingRoutine, setIsDraggingRoutine] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showRequestRoutineOverlay, setShowRequestRoutineOverlay] =
    useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const isControlled = controlledRequestRoutineVisible !== undefined;
  const isRequestRoutineVisible = isControlled
    ? controlledRequestRoutineVisible
    : showRequestRoutineOverlay;
  const closeRequestRoutine = isControlled
    ? onRequestRoutineClose ??
      (() => {
        if (__DEV__) {
          console.warn(
            'EditRoutineModal: requestRoutineVisible is controlled but onRequestRoutineClose was not provided. The overlay cannot be closed.'
          );
        }
      })
    : () => setShowRequestRoutineOverlay(false);

  const { mutateAsync: updateWorkspaceAsset } = useUpdateMutation({
    modelName: 'WorkspaceAsset',
    query: UPDATE_WORKSPACE_ASSET,
  });

  const routineData = useFilteredWorkspaceRoutines(
    {
      workspace: { eq: workspaceAsset.workspace.id },
      deactivated: { ne: true },
    },
    workspaceAsset.workspace.id,
    !workspaceAsset
  );

  const assetRoutines: string[] = useMemo(() => {
    if (!workspaceAsset.routines || workspaceAsset.routines.length === 0) {
      return [];
    }

    return workspaceAsset.routines.map((routine) => routine.id);
  }, [workspaceAsset]);

  const externalRoutines = useMemo(() => {
    if (!workspaceAsset.asset.product?.routines && !workspaceAsset.groups) {
      return [];
    }

    const allExternalRoutines: WorkspaceRoutine[] =
      extractAndDeduplicateRoutines(workspaceAsset);

    return allExternalRoutines
      .sort((a, b) => {
        return a.order - b.order;
      })
      .map((routine) => createRoutineButtonData(routine, assetRoutines));
  }, [workspaceAsset, assetRoutines]);

  const workspaceRoutines = useMemo(() => {
    if (!routineData || routineData.length === 0) {
      return [];
    }

    const eRoutines = externalRoutines || [];

    return routineData
      .filter((r) => {
        return (
          !r.deactivated &&
          !eRoutines.some((pRoutine) => pRoutine.routineId === r.id)
        );
      })
      .map((routine) => createRoutineButtonData(routine, assetRoutines));
  }, [routineData, externalRoutines, assetRoutines]);

  useEffect(() => {
    const selectedRoutines = workspaceRoutines
      .filter((routine) => routine.isAssetRoutine === true)
      .sort((a, b) => {
        let routineA, routineB;

        if (
          workspaceAsset.routineOrder &&
          workspaceAsset.routineOrder.length > 0
        ) {
          routineA = workspaceAsset.routineOrder.find(
            (x) => x.workspaceRoutineId === a.routineId
          );
          routineB = workspaceAsset.routineOrder.find(
            (x) => x.workspaceRoutineId === b.routineId
          );
        }
        const aOrder = routineA?.order ?? a.order;
        const bOrder = routineB?.order ?? b.order;
        return aOrder - bOrder;
      });

    if (workspaceRoutines.length && !initialSelectedRoutines.current) {
      const deduped = _.uniqBy(selectedRoutines, 'routineId');
      initialSelectedRoutines.current = deduped;
      setSelectedRoutines(deduped);
    }
  }, [workspaceRoutines, workspaceAsset.routineOrder, initialSelectedRoutines]);

  // Cleanup: discard any pending save queued while a request was in-flight
  useEffect(() => {
    return () => {
      pendingSaveRef.current = null;
    };
  }, []);

  const onCancel = () => {
    onClose();
  };

  const saveRoutines = useCallback(
    async (
      routineData: RoutineButtonData[],
      showSuccessMessage = true
    ): Promise<boolean> => {
      setHasChanges(true);

      if (isSavingRef.current) {
        // A request is in-flight — coalesce into a single follow-up save
        pendingSaveRef.current = { routineData, showSuccessMessage };
        return true;
      }

      isSavingRef.current = true;
      let currentData = routineData;
      let currentShowSuccess = showSuccessMessage;
      let lastResult = false;

      try {
        // Keep looping as long as new changes arrived while we were saving
        while (true) {
          const routineOrder = currentData.map((routine, i) => ({
            order: i,
            workspaceRoutineId: routine.routineId,
          }));
          const routines = currentData.map((routine) => ({
            id: routine.routineId,
          }));

          await updateWorkspaceAsset({
            input: { id: workspaceAsset.id, routineOrder, routines },
          });

          if (currentShowSuccess) {
            showSuccess(t('routines.editRoutines.saveSuccess'));
          }
          lastResult = true;

          // Pick up any changes that arrived while this request was in-flight
          const pending = pendingSaveRef.current;
          if (!pending) break;
          pendingSaveRef.current = null;
          currentData = pending.routineData;
          currentShowSuccess = pending.showSuccessMessage;
        }
      } catch (error) {
        console.error('Failed to save routines for workspace asset', {
          workspaceAssetId: workspaceAsset.id,
          error,
        });
        showError(t('routines.editRoutines.saveError'));
        lastResult = false;
      } finally {
        isSavingRef.current = false;
        pendingSaveRef.current = null;
      }

      return lastResult;
    },
    [updateWorkspaceAsset, workspaceAsset.id, showSuccess, showError, t]
  );

  const BottomBar = useMemo(() => {
    if (isWeb) {
      return null;
    }

    const backButton = (
      <Button
        variant="rounded"
        onPress={onClose}
        leftIcon={<Icon name="Arrow Left" size={24} color={Colors.role.textOnInverse} />}
        state="active"
        style={styles.backButton}
      />
    );

    const requestRoutineButton = (
      <RequestRoutineModal
        assetId={workspaceAsset.asset.id}
        workspaceId={workspaceAsset.workspace.id}
        isWeb={isWeb}
        onOpen={isWeb ? () => setShowRequestRoutineOverlay(true) : undefined}
      />
    );

    if (bottomBar) {
      const BottomBarComponent = bottomBar;
      return (
        <BottomBarComponent
          left={backButton}
          additionalHeaderBlock={requestRoutineButton}
        />
      );
    }
  }, [
    onClose,
    workspaceAsset.asset.id,
    workspaceAsset.workspace.id,
    isWeb,
    hasChanges,
    bottomBar,
    setShowRequestRoutineOverlay,
  ]);

  const modalChildren = () => (
    <SafeAreaView
      edges={['bottom', 'left', 'right']}
      style={styles.contentContainer}
    >
      <View style={{ paddingBottom: spacing.lg }}>
        <SearchInput
          value={searchTerm}
          onChangeText={setSearchTerm}
          placeholder={t(
            'routines.editRoutines.searchPlaceholder',
            'Search a routine name'
          )}
          height={40}
        />
      </View>
      <ScrollView scrollEnabled={!isDraggingRoutine}>
        <View style={{ marginBottom: 80 }}>
          <Text style={styles.routineDescription}>
            {t('routines.editRoutines.routineInWorkplace')}
          </Text>
          <RoutineSubButtons
            searchTerm={searchTerm}
            uneditableButtons={externalRoutines}
            routineButtons={workspaceRoutines}
            selectedRoutines={selectedRoutines}
            setSelectedRoutines={setSelectedRoutines}
            onSaveRoutines={saveRoutines}
            showSuccess={showSuccess}
            onDragStateChange={setIsDraggingRoutine}
          />
        </View>
      </ScrollView>
    </SafeAreaView>
  );

  return isWeb ? (
    editRoutineModalVisible ? (
      <View style={[styles.container, modalStyle]}>
        {modalChildren()}
        <View style={styles.bottomBarContainer}>{BottomBar}</View>
        <RequestRoutineModal
          assetId={workspaceAsset.asset.id}
          workspaceId={workspaceAsset.workspace.id}
          isWeb={isWeb}
          visible={isRequestRoutineVisible}
          onClose={closeRequestRoutine}
        />
      </View>
    ) : null
  ) : (
    <>
      {modalChildren()}
      {BottomBar}
    </>
  );
};

// Factory to create RoutineButtonData from routine object
const createRoutineButtonData = (
  routine: WorkspaceRoutine,
  assetRoutines?: string[]
): RoutineButtonData => {
  return {
    routineButtonText:
      routine.config.routineButtonText || routine.config.title || '',
    title: routine.config.title,
    routineId: routine.id,
    order: routine.order,
    ...(assetRoutines && {
      isAssetRoutine: assetRoutines.includes(routine.id),
    }),
  };
};

export type RoutineButtonData = {
  routineId: string;
  routineButtonText: string;
  title: string;
  order: number;
  isAssetRoutine?: boolean;
  isProductRoutine?: boolean;
};

type RoutineButtonsProps = {
  uneditableButtons: RoutineButtonData[];
  routineButtons: RoutineButtonData[];
  selectedRoutines: RoutineButtonData[];
  setSelectedRoutines: React.Dispatch<
    React.SetStateAction<RoutineButtonData[]>
  >;
  onSaveRoutines: (
    routineData: RoutineButtonData[],
    showSuccessMessage?: boolean
  ) => Promise<boolean>;
  showSuccess: (message: string) => void;
  onDragStateChange: (isDragging: boolean) => void;
  searchTerm?: string;
};

export const RoutineSubButtons: React.FC<RoutineButtonsProps> = (props) => {
  const {
    routineButtons,
    uneditableButtons,
    selectedRoutines,
    setSelectedRoutines,
    onSaveRoutines,
    showSuccess,
    onDragStateChange,
  } = props;
  const { t } = useTranslation();
  const isDraggingRef = useRef(false);

  const unselectedRoutines = useMemo(
    () => _.differenceBy(routineButtons, selectedRoutines, 'routineId'),
    [selectedRoutines, routineButtons]
  );

  const term = props.searchTerm?.toLowerCase() || '';

  const filteredSelectedRoutines = useMemo(
    () =>
      term
        ? selectedRoutines.filter(
            (r) =>
              r.title?.toLowerCase().includes(term) ||
              r.routineButtonText?.toLowerCase().includes(term)
          )
        : selectedRoutines,
    [selectedRoutines, term]
  );

  const filteredUnselectedRoutines = useMemo(
    () =>
      term
        ? unselectedRoutines.filter(
            (r) =>
              r.title?.toLowerCase().includes(term) ||
              r.routineButtonText?.toLowerCase().includes(term)
          )
        : unselectedRoutines,
    [unselectedRoutines, term]
  );

  const filteredUneditableButtons = useMemo(
    () =>
      term
        ? uneditableButtons.filter(
            (r) =>
              r.title?.toLowerCase().includes(term) ||
              r.routineButtonText?.toLowerCase().includes(term)
          )
        : uneditableButtons,
    [uneditableButtons, term]
  );

  const onRemoveRoutine = useCallback(
    (routine: RoutineButtonData, index: number) => {
      // Get updated routines after removal
      const currentRoutines = selectedRoutines as RoutineButtonData[];
      const updatedRoutines = currentRoutines.filter((_, i) => i !== index);

      // Update local state immediately for UI responsiveness
      setSelectedRoutines(updatedRoutines);

      // Save with debounce
      onSaveRoutines(updatedRoutines, false);

      // Show success message immediately
      showSuccess(
        t('routines.editRoutines.routineRemoved', { title: routine.title })
      );
    },
    [selectedRoutines, setSelectedRoutines, onSaveRoutines, showSuccess, t]
  );

  const onAppendRoutine = useCallback(
    (routine: RoutineButtonData, index: number) => {
      const currentRoutines = selectedRoutines as RoutineButtonData[];
      if (currentRoutines.some((r) => r.routineId === routine.routineId)) {
        return;
      }
      const updatedRoutines = [...currentRoutines, routine];

      setSelectedRoutines(updatedRoutines);

      // Save with debounce
      onSaveRoutines(updatedRoutines, false);

      // Show success message immediately
      showSuccess(
        t('routines.editRoutines.routineAdded', { title: routine.title })
      );
    },
    [selectedRoutines, setSelectedRoutines, onSaveRoutines, showSuccess, t]
  );

  const onMoveRoutine = useCallback(
    (e: SortableGridDragEndParams<RoutineButtonData>) => {
      onDragStateChange(false);

      const { fromIndex, toIndex } = e;

      if (fromIndex !== toIndex) {
        const currentRoutines = [...selectedRoutines];
        const [movedItem] = currentRoutines.splice(fromIndex, 1);
        currentRoutines.splice(toIndex, 0, movedItem);

        // Update local state immediately for UI responsiveness
        setSelectedRoutines(currentRoutines);

        // Save with debounce
        onSaveRoutines(currentRoutines, false);

        // Show success message immediately
        showSuccess(t('routines.editRoutines.routineReordered'));
      }
    },
    [
      selectedRoutines,
      t,
      setSelectedRoutines,
      onSaveRoutines,
      showSuccess,
      onDragStateChange,
    ]
  );

  const renderItem = ({
    item: routine,
    index,
  }: {
    item: RoutineButtonData;
    index: number;
  }) => {
    return (
      <AssignRoutineButton
        key={`selected-routine-${routine.routineId}`}
        routine={routine}
        buttonStyle={styles.selectedRoutineButton}
        onClick={() => {
          if (isDraggingRef.current) {
            isDraggingRef.current = false;
            return;
          }
          onRemoveRoutine(routine, index);
        }}
      >
        <TouchableOpacity
          style={styles.checkbox}
          onPress={() => onRemoveRoutine(routine, index)}
          activeOpacity={0.7}
        >
          <MaterialCommunityIcons
            name="checkbox-marked"
            size={24}
            color={Colors.role.textSecondary}
          />
        </TouchableOpacity>
      </AssignRoutineButton>
    );
  };

  return (
    <View style={{ marginBottom: 12 }}>
      {filteredUneditableButtons &&
        filteredUneditableButtons.length > 0 &&
        filteredUneditableButtons.map((routine, idx) => (
          <AssignRoutineButton
            key={`product-routine-${idx}`}
            routine={routine}
            buttonStyle={{
              ...styles.unselectedRoutineButton,
              backgroundColor: Colors.role.borderControl,
            }}
          />
        ))}
      {/* NOTE: Important to have GestureHandlerRootView for Android drag and drop on Modal */}
      <GestureHandlerRootView>
        <Sortable.Grid
          columnGap={10}
          columns={1}
          data={filteredSelectedRoutines}
          dropIndicatorStyle={styles.dropIndicator}
          renderItem={renderItem}
          rowGap={0}
          showDropIndicator
          overDrag={'none'}
          activeItemScale={0.99}
          keyExtractor={(item) => item.routineId}
          onDragStart={() => {
            isDraggingRef.current = true;
            onDragStateChange(true);
          }}
          onDragEnd={onMoveRoutine}
        />
      </GestureHandlerRootView>
      {filteredUnselectedRoutines?.map((routine, index) => {
        return (
          <AssignRoutineButton
            key={`unselected-routine-${routine.routineId}`}
            routine={routine}
            buttonStyle={styles.unselectedRoutineButton}
            onClick={() => onAppendRoutine(routine, index)}
          >
            <MaterialCommunityIcons
              name="checkbox-blank-outline"
              size={24}
              onPress={() => onAppendRoutine(routine, index)}
              color={Colors.role.textSecondary}
              style={styles.checkbox}
            />
          </AssignRoutineButton>
        );
      })}
    </View>
  );
};

type AssignRoutineButtonProps = {
  routine: RoutineButtonData;
  onClick?: () => void;
  buttonStyle?: StyleProp<ViewStyle>;
  leftButtonIcon?: ReactNode;
  children?: ReactNode;
};

const AssignRoutineButton = (props: AssignRoutineButtonProps) => {
  const { t } = useTranslation();

  return (
    <View style={[styles.assignButton, props.buttonStyle]}>
      <TouchableOpacity
        onPress={props.onClick}
        style={styles.routineContent}
        activeOpacity={0.7}
        testID={`assign-routine-row-${props.routine.routineId}`}
      >
        <View style={styles.labelContainer}>
          <Text style={styles.assignButtonTitle}>{props.routine.title}</Text>
          <Text style={styles.assignButtonLabel}>
            {t('routines.editRoutines.label')} {props.routine.routineButtonText}
          </Text>
        </View>
      </TouchableOpacity>
      <View style={styles.checkboxContainer}>{props.children}</View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    flex: 1,
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingBottom: 0,
    paddingVertical: Spacing.basePadding.paddingVertical,
    marginBottom: 0,
    backgroundColor: Colors.role.canvas,
  },
  bottomBarContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    paddingHorizontal: Spacing.smallPadding.paddingHorizontal,
  },
  routineDescription: {
    paddingBottom: Spacing.basePadding.base / 2,
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 16,
    color: Colors.role.textStrong,
  },
  selectedRoutineButton: {
    color: Colors.role.textSecondary,
    backgroundColor: Colors.blueLight,
    borderColor: Colors.blueLight,
    borderRadius: 0,
  },
  unselectedRoutineButton: {
    color: Colors.role.textSecondary,
    backgroundColor: Colors.role.surfaceHover,
    borderColor: Colors.role.borderSubtle,
    borderRadius: 0,
  },
  labelContainer: {
    minHeight: 36,
    flexDirection: 'column',
    justifyContent: 'center',
    flexShrink: 1,
    overflow: 'hidden',
  },
  assignButton: {
    backgroundColor: Colors.role.surface,
    borderRadius: 8,
    borderColor: Colors.role.borderControl,
    borderWidth: 1,
    minHeight: 64,
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginVertical: 4,
  },
  routineContent: {
    flex: 1,
    paddingRight: 12,
  },
  assignButtonTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.role.textStrong,
    marginBottom: 2,
  },
  assignButtonLabel: {
    fontSize: 14,
    fontWeight: '400',
    color: Colors.role.textSecondary,
  },
  checkboxContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 32,
  },
  dropIndicator: {
    backgroundColor: Colors.role.borderControl,
    borderWidth: 0,
    borderRadius: 0,
  },
  checkbox: {
    width: 24,
    height: 30,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 4,
  },
  bottomBarTitleContainer: {
    backgroundColor: Colors.role.surfaceHover,
    borderRadius: 999,
    height: 40,
    paddingHorizontal: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  bottomBarTitleText: {
    fontSize: 14,
    fontWeight: '500',
    color: Colors.role.textStrong,
  },
  backButton: {
    width: 56,
    height: 56,
    minWidth: 56,
    maxHeight: 56,
    borderRadius: 99,
    marginLeft: 16,
  },
});

const UPDATE_WORKSPACE_ASSET = `
  mutation Mutation($input: MutateWorkspaceAssetInput!) {
  updateWorkspaceAsset(input: $input) {
    id
  }
}
`;
